import React, { useState, useEffect } from "react";
import moment from "moment";
import { useQuery } from "@apollo/client/react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableHead from "@mui/material/TableHead";
import { getImage } from "src/views/shopping/Cart/CartItemFields";
import ItemId from "src/views/shopping/Orders/ItemId";
import useMutation from "src/hooks/useMutation";
import useAuth from "src/hooks/useAuth";
import {
  UPDATE_INVENTORY_REQUEST_MUTATION,
  UPDATE_INTERNAL_WORK_ORDER_MUTATION,
} from "src/constants/graphql/mutations";
import ScanOutModal from "./ScanOutModal";
import ManualLocationKit from "./manual-location-kits/ManualLocationKit";
import RaceMode from "src/components/wizard/Footer/RaceMode";
import { serverFormatSeconds } from "src/utils/dates";
import { getInventoryRequestFields } from "src/views/inventory/utils";
import Progress from "./Progress";
import {
  BY_THE_PALLET_WAREHOUSE_BUILT,
  BY_THE_PALLET,
  BY_THE_UNIT_ITEM,
  BY_THE_VAULT,
} from "src/components/project/settings/utils/billingTypes";
import AdditionalChargesModal from "./AdditionalChargesModal";
import PickingConfirmation from "./PickingConfirmation";
import useHasAdditionalCharges from "src/components/capture/ReceivingWizard/hooks/useHasAdditionalCharges";
import { FETCH_PROJECT_QUERY } from "src/constants/graphql/queries";
import BillingQuantitiesModal from "src/components/capture/ReceivingWizard/Shipment/Billing/BillingQuantitiesModal";
import useMarkStatusAs from "./hooks/useMarkStatusAs";
import PickingActionButtons from "./PickingActionButtons";
import EditPickLocationsModal from "./EditPickLocationsModal";

export default function GuidedPickingModal({
  billingType,
  inventoryRequestId,
  inventoryRequest,
  inventoryItems,
  onClose,
  onComplete,
  orderType,
  openBillingQuantities = false
}) {
  const { user } = useAuth();
  const [openBillingQuantitiesModal, setOpenBillingQuantitiesModal] = useState(openBillingQuantities);
  const [openAdditionalChargesModal, setOpenAdditionalChargesModal] = useState(false);
  const { settings } = user.company;
  const [completeLoading, setCompleteLoading] = useState(false);
  const { markAs, loading: updateItemLoading } = useMarkStatusAs(orderType);
  const [updateInventoryRequest] = useMutation(UPDATE_INVENTORY_REQUEST_MUTATION);
  const [saveInternalWorkOrder] = useMutation(UPDATE_INTERNAL_WORK_ORDER_MUTATION);
  const [currentItemIndex, setCurrentItemIndex] = useState(
    inventoryItems.findIndex(item => item.pickedStatus === false)
  );
  const [openScanModal, setOpenScanModal] = useState(false);
  const [openKitModal, setOpenKitModal] = useState(false);
  const [openEditLocationsModal, setOpenEditLocationsModal] = useState(false);
  const [kitRackLocations, setKitRackLocations] = useState([]);
  const [selectedKitAssetId, setSelectedKitAssetId] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const currentItem = inventoryItems[currentItemIndex];
  const { data: { project = {} } = {}, loading: projectLoading } = useQuery(FETCH_PROJECT_QUERY, {
    variables: { id: inventoryRequest.projectId || inventoryRequest.project?.id },
  });

  const hasAdditionalCharges = useHasAdditionalCharges(project, "outbound");
  const loading = updateItemLoading || completeLoading || projectLoading;
  const showBarcodeColumn = !!currentItem?.asset?.barcode;
  const [openPickingConfirmation, setOpenPickingConfirmation] = useState(false);
  const [postChargesAction, setPostChargesAction] = useState(null);

  useEffect(() => {
    if (!inventoryRequest.pickingStartTime && orderType === "inventoryRequest") {
      const pickingStartTime = moment().format(serverFormatSeconds);
      updateInventoryRequest({
        variables: {
          id: inventoryRequestId,
          input: getInventoryRequestFields({
            ...inventoryRequest,
            pickingStartTime,
          }),
        },
      });
    }
  }, [inventoryRequestId]);

  const handleScanComplete = barcodeMatch => {
    if (barcodeMatch) {
      markItemAsPicked();
    }
  };

  const handlePickKit = (kitAssetId, rackLocations, item) => {
    setSelectedKitAssetId(kitAssetId);
    setKitRackLocations(rackLocations);
    setSelectedItem(item);
    setOpenKitModal(true);
  };

  const handlePickKitComplete = async (values = {}) => {
    setCompleteLoading(true);
    const pickingCompleteTime = moment().format(serverFormatSeconds);

    if (orderType === "inventoryRequest") {
      await updateInventoryRequest({
        variables: {
          id: inventoryRequestId,
          input: {
            pickingCompleteTime,
            ...values,
            vaStatus: { picked: true },
          },
        },
      });
    } else if (orderType === "internalWorkOrder") {
      await saveInternalWorkOrder({
        variables: {
          id: inventoryRequestId,
          input: {
            vaStatus: { picked: true },
          },
        },
      });
    }

    onClose();
    onComplete();
  };

  const markItemAsPicked = async () => {
    if (!currentItem) return;

    try {
      await markAs(currentItem);

      if (currentItemIndex < inventoryItems.length - 1) {
        setCurrentItemIndex(currentItemIndex + 1);
      } else {
        // COMMENTED OUT: Ancillary Charges modal temporarily disabled for backend work
        // if (hasAdditionalCharges) {
        //   toggleBillingModal();
        // } else {
        // Show picking confirmation screen immediately when done picking and no charges apply
        setOpenPickingConfirmation(true);
        setPostChargesAction(() => () => handlePickKitComplete());
        // }
      }
    } catch (error) {
      console.error("Error marking item as picked:", error);
    }
  };

  const handleKitPickingComplete = () => {
    setOpenKitModal(false);
    markItemAsPicked();
  };

  const handleOverridePickLocations = item => {
    if (item?.asset?.rackLocations?.length > 1) {
      setOpenEditLocationsModal(true);
    }
  };

  const handleAdditionalChargesComplete = chargesData => {
    setOpenAdditionalChargesModal(false);
    setOpenPickingConfirmation(true);
    setPostChargesAction(() => () => {
      handlePickKitComplete(chargesData);
    });
  };

  const handlePickingConfirmationClose = () => {
    setOpenPickingConfirmation(false);
    if (postChargesAction) {
      const action = postChargesAction;
      setPostChargesAction(null);
      action();
    }
  };

  if (!currentItem) {
    return null;
  }

  const isKit = currentItem.asset?.type === "Kit";
  const photo = getImage(currentItem.asset);
  const totalItems = inventoryItems.length;

  const isAllowedOutboundBillingType = [
    BY_THE_PALLET,
    BY_THE_PALLET_WAREHOUSE_BUILT,
    BY_THE_UNIT_ITEM,
    BY_THE_VAULT,
  ].includes(billingType);

  const toggleBillingModal = () => {
    // If outbound billing type is not one of the allowed types, skip directly to Ancillary Charges
    if (!isAllowedOutboundBillingType) {
      setOpenAdditionalChargesModal(true);
      setOpenBillingQuantitiesModal(false);
      return;
    }
    setOpenBillingQuantitiesModal(v => !v);
  };

  const handleSaveBillingQuantities = () => {
    toggleBillingModal();
    setOpenAdditionalChargesModal(true);
  };

  if (openBillingQuantitiesModal && isAllowedOutboundBillingType) {
    return (
      <BillingQuantitiesModal
        type="Outbound"
        billingType={billingType}
        shipment={inventoryRequest}
        palletTypes={project?.palletTypes}
        unitTypes={project?.unitTypes}
        onSave={handleSaveBillingQuantities}
        onClose={toggleBillingModal}
      />
    );
  }

  if (openAdditionalChargesModal) {
    return (
      <AdditionalChargesModal
        inventoryRequestId={inventoryRequestId}
        inventoryRequest={inventoryRequest}
        project={project}
        inventoryItems={inventoryItems}
        onClose={onClose}
        currentItemIndex={currentItemIndex + 1}
        onSubmit={handleAdditionalChargesComplete}
      />
    );
  }

  return (
    <>
      <Dialog open fullWidth maxWidth="sm" onClose={onClose}>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography fontSize={18} fontWeight={500} color="textPrimary">
              Order Picking
            </Typography>
            <Typography color="error" fontWeight={500}>
              {currentItemIndex} out of {totalItems} Items Picked
            </Typography>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <>
            <Table sx={{ mb: 3 }}>
              <TableHead>
                <TableRow>
                  <TableCell align="center">Image</TableCell>
                  <TableCell>Item ID</TableCell>
                  {showBarcodeColumn && <TableCell>Barcode</TableCell>}
                  <TableCell>Description</TableCell>
                  <TableCell>Quantity Requested</TableCell>
                  <TableCell>Pick Location(s)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell align="center">
                    {photo ? (
                      <img style={{ maxWidth: 90, maxHeight: 60 }} src={photo} alt="" />
                    ) : (
                      "No image"
                    )}
                  </TableCell>
                  <TableCell>
                    <ItemId asset={currentItem?.asset} />
                  </TableCell>
                  {showBarcodeColumn && <TableCell>{currentItem?.asset?.barcode}</TableCell>}
                  <TableCell>{currentItem?.asset?.description}</TableCell>
                  <TableCell>{currentItem?.quantity}</TableCell>
                  <TableCell>
                    {isKit
                      ? "Various Locations"
                      : currentItem?.rackLocations?.map((rl, index) => {
                          const locationNames = (rl.warehouse_locations || [])
                            .map(loc => loc.location_name)
                            .filter(Boolean)
                            .join(', ');
                          return (
                            <Box key={index}>
                              {locationNames} ({rl.sub_qty})
                            </Box>
                          );
                        })}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </>
        </DialogContent>
        <Progress step={currentItemIndex} totalSteps={totalItems} />
        <DialogActions
          sx={{
            px: 3,
            pb: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <RaceMode
            startTime={inventoryRequest.pickingStartTime}
            completeTime={inventoryRequest.pickingCompleteTime}
          />
          <PickingActionButtons
            isKit={isKit}
            loading={loading}
            settings={settings}
            currentItem={currentItem}
            handlePickKit={handlePickKit}
            setOpenScanModal={setOpenScanModal}
            markItemAsPicked={markItemAsPicked}
            onOverridePickLocations={handleOverridePickLocations}
          />
        </DialogActions>
      </Dialog>

      {openScanModal && (
        <ScanOutModal
          asset={currentItem?.asset}
          itemId={currentItem?.asset?.itemId}
          onClose={() => setOpenScanModal(false)}
          onScanComplete={handleScanComplete}
        />
      )}

      {openKitModal && (
        <ManualLocationKit
          assetId={selectedKitAssetId}
          rackLocations={kitRackLocations}
          item={selectedItem}
          onClose={() => setOpenKitModal(false)}
          onComplete={handleKitPickingComplete}
          orderType={orderType}
        />
      )}

      {openPickingConfirmation && <PickingConfirmation onClose={handlePickingConfirmationClose} />}

      {openEditLocationsModal && currentItem && (
        <EditPickLocationsModal
          asset={currentItem.asset}
          rackLocations={currentItem.rackLocations}
          item={currentItem}
          inventoryRequest={inventoryRequest}
          onClose={() => setOpenEditLocationsModal(false)}
        />
      )}
    </>
  );
}
